import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, Area, AreaChart
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatCurrency = (value) => {
  if (!value && value !== 0) return "-";
  if (value >= 100000) return `${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toFixed(0);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-[#1A1A1A] border border-[#2A2A2A] p-3 text-sm">
      <p className="font-semibold text-white mb-2">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="flex justify-between gap-4">
          <span>{p.name}:</span>
          <span className="font-mono">{new Intl.NumberFormat("en-IN").format(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

const ClientMonthlyView = ({ clientId, clientName, currency }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/clients/${clientId}/monthly-history`)
      .then(res => {
        setTimeline(res.data.timeline || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [clientId]);

  if (loading) return <div className="p-8 text-[#71717A]">Loading trends...</div>;
  if (timeline.length === 0) return <div className="p-8 text-center text-[#71717A]">No historical data yet. Sync revenue and run billing calculations to see trends.</div>;

  const chartData = timeline.map(t => ({
    ...t,
    month: t.month.replace(/^\d{4}-/, "").replace(/^0/, "") + "/" + t.month.substring(2, 4),
  }));

  return (
    <div className="space-y-6" data-testid="client-monthly-view">
      {/* Revenue Trend */}
      <div className="card-flat p-6" data-testid="revenue-trend-chart">
        <h3 className="text-lg font-semibold mb-4">Revenue Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
            <XAxis dataKey="month" stroke="#71717A" fontSize={12} />
            <YAxis stroke="#71717A" fontSize={12} tickFormatter={formatCurrency} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Area type="monotone" dataKey="net_sales" name="Net Sales" stroke="#FFEB00" fill="#FFEB00" fillOpacity={0.15} strokeWidth={2} />
            <Area type="monotone" dataKey="expected_monthly_revenue" name="Expected Monthly" stroke="#00C853" fill="#00C853" fillOpacity={0.1} strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Billing Trend */}
      <div className="card-flat p-6" data-testid="billing-trend-chart">
        <h3 className="text-lg font-semibold mb-4">Billing Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
            <XAxis dataKey="month" stroke="#71717A" fontSize={12} />
            <YAxis stroke="#71717A" fontSize={12} tickFormatter={formatCurrency} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar dataKey="fixed_fee" name="Fixed Fee" fill="#FFEB00" radius={[2, 2, 0, 0]} />
            <Bar dataKey="variable_fee" name="Variable Fee" fill="#3B82F6" radius={[2, 2, 0, 0]} />
            <Bar dataKey="total_billable" name="Total Billable" fill="#00C853" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Commission Trend */}
      <div className="card-flat p-6" data-testid="commission-trend-chart">
        <h3 className="text-lg font-semibold mb-4">Commission Trend</h3>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
            <XAxis dataKey="month" stroke="#71717A" fontSize={12} />
            <YAxis stroke="#71717A" fontSize={12} tickFormatter={formatCurrency} />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line type="monotone" dataKey="commission" name="Commission" stroke="#FF6B6B" strokeWidth={2} dot={{ fill: '#FF6B6B', r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Ad Spend & Invoice Trend */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-flat p-6" data-testid="ad-spend-trend-chart">
          <h3 className="text-lg font-semibold mb-4">Ad Spend Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
              <XAxis dataKey="month" stroke="#71717A" fontSize={12} />
              <YAxis stroke="#71717A" fontSize={12} tickFormatter={formatCurrency} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="total_ad_spend" name="Total Ad Spend" fill="#FF4444" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card-flat p-6" data-testid="invoice-trend-chart">
          <h3 className="text-lg font-semibold mb-4">Invoice Trend</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
              <XAxis dataKey="month" stroke="#71717A" fontSize={12} />
              <YAxis stroke="#71717A" fontSize={12} tickFormatter={formatCurrency} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="invoice_total" name="Invoice Total" fill="#A78BFA" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Data Table */}
      <div className="card-flat" data-testid="monthly-data-table">
        <div className="p-4 border-b border-[#2A2A2A]">
          <h3 className="font-semibold">Month-by-Month Data</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="table-dense" style={{ minWidth: '900px' }}>
            <thead>
              <tr>
                <th>Month</th>
                <th className="text-right">Net Sales</th>
                <th className="text-right">Fixed Fee</th>
                <th className="text-right">Variable Fee</th>
                <th className="text-right">Total Billable</th>
                <th className="text-right">Commission</th>
                <th className="text-right">Ad Spend</th>
                <th className="text-right">Invoices</th>
              </tr>
            </thead>
            <tbody>
              {timeline.slice().reverse().map((t) => (
                <tr key={t.month}>
                  <td className="font-medium">{t.month}</td>
                  <td className="text-right data-number">{new Intl.NumberFormat("en-IN").format(t.net_sales)}</td>
                  <td className="text-right data-number">{new Intl.NumberFormat("en-IN").format(t.fixed_fee)}</td>
                  <td className="text-right data-number">{new Intl.NumberFormat("en-IN").format(t.variable_fee)}</td>
                  <td className="text-right data-number font-semibold" style={{color: '#FFEB00'}}>{new Intl.NumberFormat("en-IN").format(t.total_billable)}</td>
                  <td className="text-right data-number" style={{color: '#00C853'}}>{new Intl.NumberFormat("en-IN").format(t.commission)}</td>
                  <td className="text-right data-number" style={{color: '#FF4444'}}>{new Intl.NumberFormat("en-IN").format(t.total_ad_spend)}</td>
                  <td className="text-right data-number">{t.invoice_count} ({new Intl.NumberFormat("en-IN").format(t.invoice_total)})</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClientMonthlyView;
